import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Utensils, Sparkles, Loader2, CreditCard, X, Crown, Calendar, ArrowRight, Salad, Timer } from 'lucide-react';
import { toast } from 'sonner';

const GOAL_LABELS = {
  weight_loss: ['Perte de poids', 'Weight loss'],
  muscle_gain: ['Prise de masse', 'Muscle gain'],
  maintenance: ['Maintien', 'Maintenance'],
  recomposition: ['Recomposition', 'Body recomp'],
};

const COOKING_OPTS = [
  { id: 'express', title: 'Express', desc: '< 10 min' },
  { id: 'normal', fr: 'Normal', en: 'Normal', desc: '15-30 min' },
  { id: 'love', fr: "J'aime cuisiner", en: 'I love it', desc: { fr: 'Élaboré', en: 'Elaborate' } },
];

export const PlanCreditCard = ({ api, en, onViewPlan }) => {
  const [st, setSt] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null); // { amount }
  const [polling, setPolling] = useState(false);
  const [prefs, setPrefs] = useState(null); // court formulaire "Nouveau plan alimentaire"
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const loadCredits = useCallback(() => {
    api('/api/tracking/plan-credits').then(setSt).catch(() => {});
    api('/api/auth/my-calculations')
      .then((d) => setPlans((d.calculations || []).filter((c) => c.has_meal_plan).slice(0, 3)))
      .catch(() => {});
  }, [api]);
  useEffect(() => { loadCredits(); }, [loadCredits]);

  const generate = useCallback(async (overrides = {}) => {
    setBusy(true); setPay(null);
    try {
      const d = await api('/api/tracking/meal-plan/generate', {
        method: 'POST', body: JSON.stringify({ origin_url: window.location.origin, ...overrides }),
      });
      toast.success(en ? 'Your new plan is being prepared…' : 'Votre nouveau plan est en préparation…');
      setPrefs(null);
      onViewPlan(d.calculation_id);
    } catch (r) {
      if (r && r.status === 402) {
        const j = await r.json().catch(() => ({}));
        setPay({ amount: j.amount });
      } else if (r && r.status === 400) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || (en ? 'Do a free calculation first.' : "Faites d'abord un calcul gratuit."));
      } else {
        toast.error(en ? 'Generation failed' : 'Génération impossible');
      }
    } finally { setBusy(false); }
  }, [api, en, onViewPlan]);

  const openPrefs = async () => {
    try {
      const d = await api('/api/tracking/meal-plan/preferences');
      setPrefs(d);
    } catch {
      setPrefs({ meals_per_day: 3, eating_pattern: 'classic', fasting_window: '12:00-20:00', diet_preferences: '', allergies: '', cooking_time: 'normal' });
    }
  };

  const payNow = async () => {
    setBusy(true);
    try {
      const d = await api('/api/tracking/meal-plan/checkout', {
        method: 'POST', body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (d.url) window.location.href = d.url;
    } catch {
      setBusy(false);
      toast.error(en ? 'Could not start payment' : 'Paiement impossible');
    }
  };

  // Retour de Stripe → confirmer le paiement, créditer puis générer
  useEffect(() => {
    const sid = params.get('plan_credit_session');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    let stop = false;
    const tick = async () => {
      attempts += 1;
      try {
        const d = await api(`/api/tracking/meal-plan/credit-status/${sid}`);
        if (d.payment_status === 'paid') {
          params.delete('plan_credit_session');
          setParams(params, { replace: true });
          setSt(d); setPolling(false);
          toast.success(en ? 'Credit added!' : 'Crédit ajouté !');
          generate();
          return;
        }
      } catch { /* retry */ }
      if (!stop && attempts < 6) setTimeout(tick, 2000);
      else { setPolling(false); }
    };
    tick();
    return () => { stop = true; };
  }, []);

  if (!st) return null;

  const subtitle = st.premium
    ? (en
        ? `${st.monthly_included} plan/month included · ${st.included_remaining} left this month${st.purchased ? ` · +${st.purchased} extra` : ''}`
        : `${st.monthly_included} plan/mois inclus · ${st.included_remaining} restant ce mois${st.purchased ? ` · +${st.purchased} acheté(s)` : ''}`)
    : (en ? `${st.extra_price} CHF per plan` : `${st.extra_price} CHF par plan`);

  return (
    <>
    <div className="rounded-2xl bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] p-5" data-testid="plan-credit-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-primary/20 flex items-center justify-center shrink-0">
            <Utensils className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-white">{en ? 'My meal plan' : 'Mon plan alimentaire'}</h3>
            <p className="text-xs text-zinc-400 mt-0.5" data-testid="plan-credit-subtitle">{subtitle}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-primary/15 text-primary border border-primary/30 whitespace-nowrap" data-testid="plan-credit-balance">
          <Sparkles className="w-3.5 h-3.5" /> {st.balance} {en ? (st.balance === 1 ? 'credit' : 'credits') : (st.balance === 1 ? 'crédit' : 'crédits')}
        </span>
      </div>

      {polling ? (
        <div className="flex items-center justify-center gap-2 py-3 text-sm text-primary" data-testid="plan-credit-polling">
          <Loader2 className="w-4 h-4 animate-spin" /> {en ? 'Verifying your payment…' : 'Vérification de votre paiement…'}
        </div>
      ) : (
        <button onClick={openPrefs} disabled={busy}
          className="w-full py-3 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="generate-plan-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {st.balance > 0
            ? (en ? 'New meal plan' : 'Nouveau plan alimentaire')
            : (en ? `New meal plan (${st.extra_price} CHF)` : `Nouveau plan alimentaire (${st.extra_price} CHF)`)}
        </button>
      )}

      {!st.premium && (
        <button onClick={() => nav('/mon-espace?tab=profil&plan=monthly')} className="mt-3 w-full text-xs text-zinc-400 hover:text-white transition-colors inline-flex items-center justify-center gap-1.5" data-testid="plan-credit-upsell">
          <Crown className="w-3.5 h-3.5 text-yellow-400" /> {en ? 'Get 1 plan/month included with Suivi Pro' : '1 plan/mois inclus avec Suivi Pro'}
        </button>
      )}

      {/* Mes derniers plans */}
      {plans.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="recent-plans">
          <p className="text-xs font-bold tracking-wider text-zinc-500 mb-2">{en ? 'MY RECENT PLANS' : 'MES DERNIERS PLANS'}</p>
          <div className="space-y-2">
            {plans.map((c) => (
              <button key={c.calculation_id} onClick={() => onViewPlan(c.calculation_id)}
                className="w-full flex items-center justify-between gap-3 rounded-xl bg-zinc-800/50 border border-white/10 px-3 py-2.5 hover:border-primary/40 transition-colors text-left group"
                data-testid="recent-plan-item">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="text-sm text-zinc-300 truncate">
                    {(GOAL_LABELS[c.goal] || [c.goal, c.goal])[en ? 1 : 0]}
                    <span className="text-zinc-500"> · {Math.round(c.target_calories)} kcal</span>
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>

    {createPortal(
      <>
      {/* Nouveau plan alimentaire : court formulaire — uniquement les préférences propres
          au plan (repas/jour, régime, allergies, cuisine), le profil de base (âge/poids/
          objectif/morphotype/activité) est repris automatiquement du dernier calcul. */}
      <AnimatePresence>
        {prefs && !pay && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 sm:px-4" data-testid="meal-plan-prefs-modal"
            onClick={() => setPrefs(null)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-2xl p-6 max-w-md w-full relative max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sm:hidden w-10 h-1.5 rounded-full bg-white/15 mx-auto mb-4" />
              <button onClick={() => setPrefs(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="meal-plan-prefs-close"><X className="w-5 h-5" /></button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500/20 to-primary/20 flex items-center justify-center mb-4"><Salad className="w-6 h-6 text-primary" /></div>
              <h4 className="text-lg font-bold text-white mb-1">{en ? 'New meal plan' : 'Nouveau plan alimentaire'}</h4>
              <p className="text-sm text-zinc-400 mb-5">
                {en ? "We reuse your profile (age, weight, goal, activity) — just confirm your meal preferences." : 'On reprend votre profil (âge, poids, objectif, activité) — confirmez juste vos préférences de repas.'}
              </p>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1.5"><Utensils className="w-3.5 h-3.5 text-primary" /> {en ? 'Meals per day' : 'Repas par jour'}</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setPrefs((p) => ({ ...p, meals_per_day: n }))}
                        className={`py-2.5 rounded-lg text-sm font-bold border transition-all ${prefs.meals_per_day === n ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400'}`}
                        data-testid={`prefs-meals-${n}`}>{n}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1.5"><Timer className="w-3.5 h-3.5 text-primary" /> {en ? 'Eating pattern' : 'Rythme alimentaire'}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ id: 'classic', fr: 'Classique', en: 'Classic' }, { id: 'intermittent', fr: 'Jeûne intermittent', en: 'Intermittent' }].map((p) => (
                      <button key={p.id} type="button" onClick={() => setPrefs((s) => ({ ...s, eating_pattern: p.id }))}
                        className={`py-2.5 rounded-lg text-xs font-semibold border transition-all ${prefs.eating_pattern === p.id ? 'bg-gradient-to-r from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400'}`}
                        data-testid={`prefs-pattern-${p.id}`}>{en ? p.en : p.fr}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1.5"><Salad className="w-3.5 h-3.5 text-primary" /> {en ? 'Diet / allergies (optional)' : 'Régime / allergies (optionnel)'}</label>
                  <textarea value={prefs.diet_preferences} rows={2} onChange={(e) => setPrefs((p) => ({ ...p, diet_preferences: e.target.value }))}
                    placeholder={en ? 'Vegetarian, gluten-free…' : 'Végétarien, sans gluten…'}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-primary focus:outline-none resize-none mb-2"
                    data-testid="prefs-diet-input" />
                  <textarea value={prefs.allergies} rows={1} onChange={(e) => setPrefs((p) => ({ ...p, allergies: e.target.value }))}
                    placeholder={en ? 'Nuts, lactose…' : 'Fruits à coque, lactose…'}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-primary focus:outline-none resize-none"
                    data-testid="prefs-allergies-input" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-2">{en ? 'Cooking time' : 'Temps en cuisine'}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {COOKING_OPTS.map((c) => (
                      <button key={c.id} type="button" onClick={() => setPrefs((p) => ({ ...p, cooking_time: c.id }))}
                        className={`py-2.5 px-1 rounded-lg border text-center transition-all ${prefs.cooking_time === c.id ? 'bg-gradient-to-r from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400'}`}
                        data-testid={`prefs-cooking-${c.id}`}>
                        <span className="block text-xs font-semibold">{c.id === 'express' ? 'Express' : (en ? c.en : c.fr)}</span>
                        <span className="block text-[10px] opacity-70 mt-0.5">{typeof c.desc === 'string' ? c.desc : (en ? c.desc.en : c.desc.fr)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <button onClick={() => generate(prefs)} disabled={busy}
                className="w-full mt-6 py-3 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="prefs-confirm-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {st.balance > 0
                  ? (en ? 'Generate my plan' : 'Générer mon plan')
                  : (en ? `Generate my plan (${st.extra_price} CHF)` : `Générer mon plan (${st.extra_price} CHF)`)}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation de paiement (plus de crédit) */}
      <AnimatePresence>
        {pay && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="plan-pay-modal"
            onClick={() => setPay(null)}>
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
              className="bg-zinc-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setPay(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="plan-pay-close"><X className="w-5 h-5" /></button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500/20 to-primary/20 flex items-center justify-center mb-4"><CreditCard className="w-6 h-6 text-primary" /></div>
              <h4 className="text-lg font-bold text-white mb-1">{en ? 'No credit left' : 'Plus de crédit disponible'}</h4>
              <p className="text-sm text-zinc-400 mb-5">
                {en
                  ? `Generate a brand new 7-day meal plan with recipes & AI photos for ${pay.amount} CHF.`
                  : `Générez un tout nouveau plan 7 jours avec recettes & photos IA pour ${pay.amount} CHF.`}
              </p>
              <button onClick={payNow} disabled={busy}
                className="w-full py-3 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="plan-pay-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {en ? `Pay ${pay.amount} CHF` : `Payer ${pay.amount} CHF`}
              </button>
              <p className="text-center text-[11px] text-zinc-500 mt-3">{en ? 'Secure Stripe payment' : 'Paiement sécurisé Stripe'}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>,
      document.body
    )}
    </>
  );
};

export default PlanCreditCard;
